import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { IconButton } from "@nexus/ui";
import { useSidePanel } from "@/contexts/SidePanelContext";
import { cn } from "@/lib/utils";

/** Non-modal contextual panel: focus enters on open; the surrounding page stays usable. */
export function SectionSidePanel() {
  const { isOpen, title, content, close } = useSidePanel();
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    closeRef.current?.focus();
    return () => {
      // Do not steal focus if the user deliberately moved back into the page.
      if (
        opener?.isConnected &&
        (panel?.contains(document.activeElement) || document.activeElement === document.body)
      )
        opener.focus();
    };
  }, [isOpen]);

  return (
    <aside
      ref={panelRef}
      role="dialog"
      aria-label={title || "Details"}
      aria-hidden={!isOpen}
      inert={!isOpen}
      data-escape-key-handled="true"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        // Nested menus/dialogs own their Escape; React portals bubble here too.
        if (
          event.target instanceof Element &&
          event.target.closest('[data-escape-key-handled="true"]') !== event.currentTarget
        )
          return;
        event.preventDefault();
        event.stopPropagation();
        close();
      }}
      className={cn(
        "fixed top-14 right-0 z-40 h-[calc(100dvh-3.5rem)] w-full sm:w-[440px] lg:w-[500px]",
        "flex flex-col border-l border-border bg-surface shadow-dialog transition-transform duration-200 motion-reduce:transition-none",
        isOpen ? "translate-x-0" : "translate-x-full shadow-none",
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="min-w-0 break-words text-sm font-semibold text-primary">
          {title || "Details"}
        </h2>
        <IconButton ref={closeRef} label="Close panel" onClick={close}>
          <X className="h-4 w-4" aria-hidden />
        </IconButton>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">{content}</div>
    </aside>
  );
}
